import React, { useEffect, useState } from "react";
import { currentUrl } from "../../App";
import { useLocation } from "react-router-dom";
import { Button, Card, Col, Container, InputGroup, Row, Spinner } from "react-bootstrap";
import { fetchBeds, fetchPossibleBedOccupants, fetchRooms } from "../../services/httpClient";
import PageTitle from "../ui/PageTitle";
import BedAssignmentCard from "./BedAssignmentCard";
import SelectObject from "../ui/SelectObject";
import useApp from "../../utils/useApp";

interface BedAssignmentState {
  setOrgToSave: any;
  setShowToast: any;
  setToastText: any;
  setToastBody: any;
  getData: boolean;
  errMsg: any;
  setErrMsg: any;
}

const BedAssignment = ({ setOrgToSave, setShowToast, setToastText, setToastBody, getData, errMsg, setErrMsg }: BedAssignmentState) => {
  const location = useLocation();
  currentUrl.value = location.pathname;
  const { auth, showMenu }: any = useApp();
  const [filterRoom, setFilterRoom] = useState<any>();
  const [rooms, setRooms] = useState<any[]>([]);
  const [beds, setBeds] = useState<any[]>([]);
  const [filteredBeds, setFilteredBeds] = useState<any[]>([]);
  const [vacantBeds, setVacantBeds] = useState<any[]>([]);
  const [filteredVacantBeds, setFilteredVacantBeds] = useState<any[]>([]);
  const [possibleOccupants, setPossibleOccupants] = useState<any[]>([]);
  const [viewVacant, setViewVacant] = useState<boolean>(false);
  const [viewUnassigned, setViewUnassigned] = useState<boolean>(false);
  const [viewAll, setViewAll] = useState<boolean>(true);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (getData) {
      setIsLoading(true);
      setViewVacant(false);
      setViewAll(true);
      getBeds(false);
    }
  }, [getData, auth?.profile?.userOrgID]);

  const getBeds = (maintainFilter: boolean) => {
    fetchBeds()
      .then((res: any) => {
        const bedList = res[0].data;
        bedList.sort((a: any, b: any) => {
          // group by room name
          if (a.room_name !== b.room_name) {
            return a.room_name.localeCompare(b.room_name, undefined, { numeric: true, sensitivity: "base" });
          }
          // sort beds in each room alphanumerically
          return a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: "base" });
        });
        setBeds(bedList);

        const vacantBedList = bedList.filter((bed: any) => bed.occupant_id === null);
        setVacantBeds(vacantBedList);

        if (maintainFilter) {
          if (filterRoom) {
            const filtered = bedList.filter((bed: any) => bed.room_id === filterRoom?.id);
            setFilteredBeds(filtered);
            const filteredVacant = vacantBedList.filter((bed: any) => bed.room_id === filterRoom?.id);
            setFilteredVacantBeds(filteredVacant);
          } else {
            setFilteredBeds(bedList);
            setFilteredVacantBeds(vacantBedList);
          }
        } else {
          setFilteredBeds(bedList);
          setFilteredVacantBeds(vacantBedList);
        }

        getPossibleOccupants(bedList);
        getRooms(bedList, vacantBedList);
      })
      .catch((err: any) => {
        console.log(err);
      })
      .finally(() => {
        setTimeout(() => {
          setIsLoading(false);
        }, 150);
      });
  };

  const getRooms = (bedList: any, vacantBedList: any) => {
    fetchRooms()
      .then((res: any) => {
        let roomList = res[0].data;
        roomList = roomList.map((room: any) => {
          const bedCount = bedList.filter((bed: any) => bed.room_id === room.id).length;
          return {
            ...room,
            name: `${room.name} (${bedCount} beds)`,
          };
        });

        roomList.sort((a: any, b: any) => {
          return a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: "base" });
        });
        setRooms(roomList);

        if (roomList.length === 1) {
          setFilterRoom(roomList[0]);
          const roomId = roomList[0].id;
          const filtered = bedList.filter((bed: any) => bed.room_id === roomId);
          setFilteredBeds(filtered);
          const filteredVacant = vacantBedList.filter((bed: any) => bed.room_id === roomId);
          setFilteredVacantBeds(filteredVacant);
        }
      })
      .catch((err: any) => {
        console.log(err);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const filterByRoom = (e: any) => {
    const room = rooms.find((room: any) => room.key === e.target.value);
    setFilterRoom(room);
    setViewVacant(false);
    setViewAll(true);

    if (e.target.value === "0") {
      setFilteredBeds(beds);
      setFilteredVacantBeds(vacantBeds);
    } else {
      const roomId = rooms.find((room: any) => room.key === e.target.value)?.id;
      const filtered = beds.filter((bed: any) => bed.room_id === roomId);
      setFilteredBeds(filtered);
      const filteredVacant = vacantBeds.filter((bed: any) => bed.room_id === roomId);
      setFilteredVacantBeds(filteredVacant);
    }
  };

  const getPossibleOccupants = (bedList: any) => {
    fetchPossibleBedOccupants().then((res: any) => {
      var initialPossible = res[0].data;

      var possible = initialPossible.map((f: any) => {
        // Find the matching bed entry for this occupant
        const match = bedList.find((b: any) => b.occupant_type === f.type && b.occupant_id === f.id);

        // Build name with room/bed if found
        const nameWithBed = f.firstname + " " + f.lastname + (match ? ` - ${match.room_name} (${match.name})` : "");

        return {
          ...f,
          name: nameWithBed,
          key: f.id + f.type,
          hasBed: !!match,
        };
      });

      // Sort so those with beds go last
      possible.sort((a: any, b: any) => {
        if (a.hasBed === b.hasBed) return 0;
        return a.hasBed ? 1 : -1;
      });

      setPossibleOccupants(possible);
    });
  };

  const onClickViewVacant = () => {
    setViewVacant(true);
    setViewAll(false);
  };

  const onClickViewAll = () => {
    setViewVacant(false);
    setViewAll(true);
  };

  const OnClickChangeView = () => {
    setViewUnassigned(!viewUnassigned);
  };

  return (
    <>
      {isLoading ? (
        <Container>
          <Button size="sm" className="spinner-button spinner-button-main">
            <Spinner animation="border" style={{ color: "#F6893D" }} />
          </Button>
        </Container>
      ) : (
        <>
          <p className={errMsg ? "errmsg" : "offscreen"} aria-live="assertive">
            {errMsg}
          </p>
          {!viewUnassigned ? (
            <>
              {/* <PageTitle
                title="Bed Assignment"
                type="section"
                buttonColor="primary"
                buttonTitle="Unassigned Population"
                onButtonClick={() => OnClickChangeView()}
              /> */}
              <PageTitle title="Bed Assignment" type="section" />
              <Row className={showMenu ? "my-3 sticky-top card-note-menu-open-sticky" : "my-3 sticky-top card-note-sticky"}>
                <Col xs={12} lg={10}>
                  <Card className="bg-light">
                    <Row className="pe-0 mb-3 mt-3 ms-1 me-1">
                      <Col xs={6}>
                        <SelectObject
                          label="Select a Room"
                          value={filterRoom?.key}
                          options={rooms}
                          onUpdate={(e: any) => filterByRoom(e)}
                          initialOption="View All"
                        />
                      </Col>
                      <Col xs={6} className="d-flex justify-content-end align-items-center">
                        <InputGroup className="d-flex justify-content-end align-items-center">
                          <Button
                            className={
                              viewVacant ? "app-primary-color me-2 button rounded" : "app-primary-color-inverse me-2 button rounded"
                            }
                            onClick={() => onClickViewVacant()}
                          >
                            Vacant
                          </Button>
                          <Button
                            className={viewAll ? "app-primary-color me-2 button rounded" : "app-primary-color-inverse me-2 button rounded"}
                            onClick={() => onClickViewAll()}
                          >
                            All
                          </Button>
                        </InputGroup>
                      </Col>
                    </Row>
                  </Card>
                </Col>
              </Row>
              {viewVacant ? (
                <Row className="pb-5 mt-2">
                  <Col xs={12} lg={10}>
                    {filteredVacantBeds.length === 0 ? (
                      <p className="mt-2">No Vacant Beds</p>
                    ) : (
                      filteredVacantBeds?.map((item: any, index: number) => (
                        <Row key={index}>
                          <Col xs={12}>
                            <Row className="mb-1"></Row>
                            <BedAssignmentCard
                              item={item}
                              getItems={getBeds}
                              beds={vacantBeds}
                              possibleOccupants={possibleOccupants}
                              setErrMessage={setErrMsg}
                              setShowToast={setShowToast}
                              setToastText={setToastText}
                              setToastBody={setToastBody}
                            />
                          </Col>
                        </Row>
                      ))
                    )}
                  </Col>
                </Row>
              ) : (
                <Row className="pb-5 mt-2">
                  <Col xs={12} lg={10}>
                    {filteredBeds.length === 0 ? (
                      <p className="mt-2">No Beds Created</p>
                    ) : (
                      filteredBeds?.map((item: any) => (
                        <>
                          <Row>
                            <Col xs={12}>
                              <Row className="mb-1"></Row>
                              <BedAssignmentCard
                                item={item}
                                getItems={getBeds}
                                beds={beds}
                                possibleOccupants={possibleOccupants}
                                setErrMessage={setErrMsg}
                                setShowToast={setShowToast}
                                setToastText={setToastText}
                                setToastBody={setToastBody}
                              />
                            </Col>
                          </Row>
                        </>
                      ))
                    )}
                  </Col>
                </Row>
              )}
              <Row className="mb-5"></Row>
            </>
          ) : (
            <>
              {(() => {
                const unassigned = possibleOccupants.filter((p: any) => !p.hasBed);
                if (unassigned.length === 0) {
                  return (
                    <>
                      <PageTitle
                        title="Unassigned Population"
                        type="section"
                        buttonColor="primary"
                        buttonTitle="Back"
                        onButtonClick={() => OnClickChangeView()}
                      />
                      <p className="mt-2">No unassigned people found</p>
                    </>
                  );
                }
                return (
                  <>
                    <PageTitle
                      title={`Unassigned Population (${unassigned.length})`}
                      type="section"
                      buttonColor="primary"
                      buttonTitle="Back"
                      onButtonClick={() => OnClickChangeView()}
                    />
                    <Row className="mt-3">
                      {unassigned.map((p: any, i: number) => (
                        <Col xs={12} lg={10}>
                          <Card key={p.key || i} className="mb-2">
                            <Card.Body>
                              <Row>
                                <Col>
                                  <h6 className="mb-0">{p.name}</h6>
                                </Col>
                              </Row>
                            </Card.Body>
                          </Card>
                        </Col>
                      ))}
                    </Row>
                  </>
                );
              })()}
            </>
          )}
        </>
      )}
    </>
  );
};

export default BedAssignment;
